import type { Vehicle } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { Banner, BannerContent, BannerIcon, BannerTitle, Table, Typography } from '@cobliteam/ui/components';
import Button from '@cobliteam/ui/components/Button';
import Dialog from '@cobliteam/ui/components/Dialog';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@cobliteam/ui/components/Table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cobliteam/ui/form';
import Input from '@cobliteam/ui/form/Input';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import VehicleDialogView from '@cobliteam/vehicle/src/components/VehicleDialogView';
import getValue from 'get-value';
import { useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import type { CobliChatUIMessagePart } from '../../types';

interface VehiclesListToolProps {
  part: CobliChatUIMessagePart;
}

// Helper function to check if value is a leaf (non-object or special object types)
const isLeafValue = (value: any): boolean => {
  return value === null || value === undefined || typeof value !== 'object' || Array.isArray(value) || value instanceof Date;
};

// Helper function to process array values
const processArrayKeys = (arr: any[], prefix: string): string[] => {
  if (arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null) {
    return getAllLeafKeys(arr[0], prefix);
  }
  return [];
};

// Helper function to process object properties
const processObjectKeys = (obj: Record<string, any>, prefix: string): string[] => {
  const keys: string[] = [];
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      // If value is a leaf, add the key; otherwise recurse
      if (isLeafValue(value)) {
        keys.push(fullKey);
      } else {
        keys.push(...getAllLeafKeys(value, fullKey));
      }
    }
  }
  return keys;
};

// Helper function to get all leaf keys from an object (only non-object values)
const getAllLeafKeys = (obj: any, prefix = ''): string[] => {
  if (obj === null || obj === undefined) return [];
  if (Array.isArray(obj)) return processArrayKeys(obj, prefix);
  if (typeof obj === 'object') return processObjectKeys(obj, prefix);
  return [];
};

// Helper function to get value from nested path using get-value package
const getNestedValue = (obj: any, path: string): any => {
  return getValue(obj, path);
};

// Helper function to format boolean value
const formatBoolean = (value: boolean): string => {
  return value ? 'Sim' : 'Não';
};

// Helper function to format number value
const formatNumber = (value: number): string => {
  // Format numbers with 6 decimal places if they look like coordinates
  if (Math.abs(value) < 1000 && value % 1 !== 0) {
    return value.toFixed(6);
  }
  return value.toString();
};

// Helper function to format object value
const formatObject = (value: any): string => {
  if (Array.isArray(value)) return `[${value.length} items]`;
  if (value instanceof Date) return value.toLocaleString();
  return JSON.stringify(value);
};

// Helper function to format cell value
const formatCellValue = (value: any): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return formatBoolean(value);
  if (typeof value === 'number') return formatNumber(value);
  if (typeof value === 'object') return formatObject(value);
  return String(value);
};

// Helper function to get column type
const getColumnType = (vehicles: any[], columnPath: string): 'boolean' | 'number' | 'string' => {
  for (const vehicle of vehicles) {
    const value = getNestedValue(vehicle, columnPath);
    if (value !== null && value !== undefined) {
      if (typeof value === 'boolean') return 'boolean';
      if (typeof value === 'number') return 'number';
      return 'string';
    }
  }
  return 'string';
};

// Helper function to check if value matches filter
const matchesFilter = (value: any, filter: string, columnType: 'boolean' | 'number' | 'string'): boolean => {
  if (!filter || filter === '') return true;

  if (value === null || value === undefined) {
    return filter === 'null' || filter === '';
  }

  const formattedValue = formatCellValue(value).toLowerCase();
  const filterLower = filter.toLowerCase();

  if (columnType === 'boolean') {
    if (filter === 'true') return value === true;
    if (filter === 'false') return value === false;
    return true; // 'all' or empty
  }

  return formattedValue.includes(filterLower);
};

// Helper function to compare values for sorting
const compareValues = (a: any, b: any, columnType: 'boolean' | 'number' | 'string'): number => {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  if (columnType === 'number') {
    return (a as number) - (b as number);
  }

  if (columnType === 'boolean') {
    return a === b ? 0 : a ? 1 : -1;
  }

  return String(a).localeCompare(String(b));
};

type SortDirection = 'asc' | 'desc' | null;

const VehiclesListTool = ({ part }: VehiclesListToolProps) => {
  const { t } = useTranslation('vehicles');
  const output = (part as any).output;
  const vehicles = output?.data || [];
  const totalFiltered = output?.totalFiltered || 0;
  const total = output?.total || 0;

  // State for filters (column path -> filter value)
  const [filters, setFilters] = useState<Record<string, string>>({});

  // State for sorting (column path -> direction)
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // State for dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // State for vehicle drawer
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Preview limit
  const PREVIEW_ROWS = 5;

  // Get all unique leaf keys from all vehicles
  const columns = useMemo(() => {
    if (vehicles.length === 0) return [];

    const allKeysSet = new Set<string>();
    for (const vehicle of vehicles) {
      const keys = getAllLeafKeys(vehicle);
      for (const key of keys) {
        allKeysSet.add(key);
      }
    }

    // Convert to array and sort (prioritize top-level keys, then nested)
    return Array.from(allKeysSet).sort((a, b) => {
      const aDepth = a.split('.').length;
      const bDepth = b.split('.').length;
      if (aDepth !== bDepth) return aDepth - bDepth;
      return a.localeCompare(b);
    });
  }, [vehicles]);

  // Helper function to get translated header label
  const getHeaderLabel = (path: string): string => {
    // Convert path to translation key format
    // e.g., "device.lastLocation.latitude" -> "device.last_location.latitude"

    const translationKey = path
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .split('.')
      .map((part) => part.replace(/^_/, ''))
      .join('.');

    // Try to get translation, fallback to formatted key
    const translationPath = `fields.${translationKey}`;
    const translated = t(translationPath);

    // If translation exists and is different from the key, use it
    if (translated && translated !== translationPath) {
      return translated;
    }

    // Fallback: format the last part of the path
    const lastPart = path.split('.').pop() || path;
    return lastPart
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Filter columns to exclude 'id' and ensure licensePlate is first
  const columnsToShow = useMemo(() => {
    const filtered = columns.filter((column) => {
      return column !== 'id' && column !== 'driver.id' && column !== 'device.id' && column !== 'device.cobliId';
    });

    // Find licensePlate column
    const licensePlateIndex = filtered.findIndex((col) => col === 'licensePlate');

    if (licensePlateIndex > 0) {
      // Move licensePlate to the beginning
      const reordered = [filtered[licensePlateIndex], ...filtered.filter((_, idx) => idx !== licensePlateIndex)];
      return reordered;
    }

    return filtered;
  }, [columns]);

  // Get column types for all columns
  const columnTypes = useMemo(() => {
    const types: Record<string, 'boolean' | 'number' | 'string'> = {};
    for (const column of columnsToShow) {
      types[column] = getColumnType(vehicles, column);
    }
    return types;
  }, [columnsToShow, vehicles]);

  // Filter and sort vehicles
  const filteredAndSortedVehicles = useMemo(() => {
    let result = [...vehicles];

    // Apply filters
    result = result.filter((vehicle) => {
      for (const column of columnsToShow) {
        const filter = filters[column];
        if (filter && filter !== '') {
          const value = getNestedValue(vehicle, column);
          const columnType = columnTypes[column];
          if (!matchesFilter(value, filter, columnType)) {
            return false;
          }
        }
      }
      return true;
    });

    // Apply sorting
    if (sortColumn && sortDirection) {
      const columnType = columnTypes[sortColumn];
      result.sort((a, b) => {
        const aValue = getNestedValue(a, sortColumn);
        const bValue = getNestedValue(b, sortColumn);
        const comparison = compareValues(aValue, bValue, columnType);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [vehicles, filters, sortColumn, sortDirection, columnsToShow, columnTypes]);

  // Handle filter change
  const handleFilterChange = (column: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  // Handle sort click
  const handleSortClick = (column: string) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Render table component
  const renderTable = (vehiclesToShow: any[], showAll = false) => {
    const displayVehicles = showAll ? vehiclesToShow : vehiclesToShow.slice(0, PREVIEW_ROWS);
    const hasMoreRows = !showAll && vehiclesToShow.length > PREVIEW_ROWS;

    return (
      <div className='mb-4 overflow-x-auto overflow-y-auto rounded-md bg-gray-3' style={{ maxHeight: showAll ? '70vh' : 'auto' }}>
        <Table className='w-full'>
          <TableHeader className='sticky top-0 z-10 rounded-md bg-gray-3'>
            {/* Header row */}
            <TableRow>
              {columnsToShow.map((key, index) => {
                const isSorted = sortColumn === key;
                const isLastColumn = index === columnsToShow.length - 1;
                const isFirstColumn = index === 0;
                return (
                  <TableHead
                    key={key}
                    className={cn(
                      'cursor-pointer select-none border-gray-6 border-t border-b bg-gray-3 px-4 text-left font-semibold text-sm hover:bg-gray-4',
                      isFirstColumn && 'rounded-tl-md border-l',
                      isLastColumn && 'rounded-tr-md border-r',
                      !(isFirstColumn || isLastColumn) && 'border-r-0 border-l-0'
                    )}
                    onClick={() => handleSortClick(key)}
                  >
                    <div className='flex items-center gap-2'>
                      <span>{getHeaderLabel(key)}</span>
                      {isSorted && <Icon name={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'} size='sm' className='flex-shrink-0 text-primary-9' />}
                      {!isSorted && <Icon name='chevrons-up-down' size='sm' className='flex-shrink-0 text-gray-9' />}
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
            {/* Filter row */}
            <TableRow className='bg-gray-3'>
              {columnsToShow.map((key, index) => {
                const columnType = columnTypes[key];
                const filterValue = filters[key] || '';
                const isLastColumn = index === columnsToShow.length - 1;

                if (columnType === 'boolean') {
                  return (
                    <TableCell key={key} className={cn('border border-gray-6 bg-gray-2 px-4 py-2', isLastColumn && 'border-r')} onClick={(e) => e.stopPropagation()}>
                      <Select value={filterValue} onValueChange={(value) => handleFilterChange(key, value)}>
                        <SelectTrigger className='w-full' size='sm'>
                          <SelectValue placeholder='Todos' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='all' defaultChecked>
                            Todos
                          </SelectItem>
                          <SelectItem value='true'>Sim</SelectItem>
                          <SelectItem value='false'>Não</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  );
                }

                return (
                  <TableCell key={key} className={cn('border border-gray-6 bg-gray-2 px-4 py-2', isLastColumn && 'border-r')} onClick={(e) => e.stopPropagation()}>
                    <Input
                      type={columnType === 'number' ? 'number' : 'text'}
                      value={filterValue}
                      onChange={(e) => handleFilterChange(key, e.target.value)}
                      placeholder='Filtrar...'
                      size='sm'
                      className='w-full'
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayVehicles.map((vehicle: any, idx: number) => (
              <TableRow key={vehicle.id || idx} isEven={idx % 2 === 0}>
                {columnsToShow.map((key, colIndex) => {
                  const value = getNestedValue(vehicle, key);
                  const formattedValue = formatCellValue(value);
                  const isLastColumn = colIndex === columnsToShow.length - 1;

                  // Add clickable licensePlate column to open drawer
                  if (key === 'licensePlate' && vehicle.id) {
                    return (
                      <TableCell key={key} className={cn('border border-gray-6 px-4 py-2 text-sm', isLastColumn && 'border-r')}>
                        <button
                          type='button'
                          onClick={() => setSelectedVehicle(vehicle as Vehicle)}
                          className='cursor-pointer text-primary-9 hover:text-primary-10 hover:underline'
                        >
                          {formattedValue}
                        </button>
                      </TableCell>
                    );
                  }

                  if (vehicle.driver?.id) {
                    return (
                      <TableCell key={key} className={cn('border border-gray-6 px-4 py-2 text-sm', isLastColumn && 'border-r')}>
                        <a
                          href={`/driver-management?driverId=${vehicle.driver.id}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-primary-9 hover:text-primary-10 hover:underline'
                        >
                          {formattedValue}
                        </a>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell key={key} className={cn('border border-gray-6 px-4 py-2 text-sm', isLastColumn && 'border-r')}>
                      {formattedValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {hasMoreRows && (
              <TableRow>
                <TableCell colSpan={columnsToShow.length} className='border border-gray-6 px-4 py-3 text-center text-gray-10 text-sm'>
                  <div className='flex items-center justify-center gap-2'>... e mais {vehiclesToShow.length - PREVIEW_ROWS} veículos.</div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    );
  };

  if ((part as any).output.error) {
    return null;
  }

  if (vehicles.length === 0) {
    return (
      <div className='mt-4'>
        <h2 className='mb-4 font-semibold text-lg'>{t('labels.vehicle')}</h2>
        <p className='text-gray-8 text-sm'>{t('status.no_vehicles')}</p>
      </div>
    );
  }

  return (
    <>
      <div className='mt-4'>
        <div className='mb-5 flex items-center justify-between gap-4'>
          <div>
            <h2 className='font-semibold text-xl'>Lista de Veículos</h2>
            <p>{(part as any).output.title}</p>
          </div>
          {filteredAndSortedVehicles.length > PREVIEW_ROWS && (
            <Button variant='contained' color='primary' size='sm' onClick={() => setIsDialogOpen(true)} startIcon={<Icon name='maximize-2' />}>
              Visualizar
            </Button>
          )}
        </div>
        {renderTable(filteredAndSortedVehicles, false)}
      </div>

      {/* Full table dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <Dialog.Content size='lg' className='z-10 max-w-[95vw]' portal={false}>
          <Dialog.Header>
            <Dialog.Title>
              <div>
                <h2 className='font-semibold text-2xl'> Lista de Veículos</h2>
                <p>{(part as any).output.title}</p>
              </div>
            </Dialog.Title>
            <Dialog.Description className='sr-only'>Tabela completa de veículos</Dialog.Description>
            <Dialog.Close />
          </Dialog.Header>
          <Dialog.Body className='mt-2 h-[80vh] overflow-auto'>
            {renderTable(filteredAndSortedVehicles, true)}
            <div className='mb-4'>
              <p className='text-gray-8 text-sm'>
                Mostrando {filteredAndSortedVehicles.length} de {totalFiltered} veículos (Total: {total})
              </p>
            </div>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog>

      {/* Vehicle dialog */}
      {selectedVehicle && <VehicleDialogView vehicle={selectedVehicle} vehicleGroups={[]} onClose={() => setSelectedVehicle(null)} />}
    </>
  );
};

const VehiclesListToolError = () => {
  return (
    <div className='my-4'>
      <Banner color='danger'>
        <BannerIcon icon={<Icon color='danger' name='x' />} />
        <BannerContent>
          <BannerTitle>Ocorreu um erro ao carregar os veículos</BannerTitle>
          <Typography>Ocorreu um erro ao carregar os veículos. Por favor, tente novamente mais tarde.</Typography>
        </BannerContent>
      </Banner>
    </div>
  );
};

export const VehiclesSkeleton = () => {
  // Simulate 2 columns (Placa and Ignição Ligada)
  const columnCount = 2;
  const rowCount = 5;

  return (
    <div className='mt-4'>
      {/* Header Section */}
      <div className='mb-5 flex items-center justify-between gap-4'>
        <div className='flex flex-col gap-2'>
          <Skeleton className='h-7 w-48' /> {/* Title */}
          <Skeleton className='h-5 w-64' /> {/* Subtitle */}
        </div>
        <Skeleton className='h-8 w-24 rounded-md' /> {/* Visualizar button */}
      </div>
      {/* Table Container */}
      <div className='mb-10 overflow-x-auto rounded-md bg-gray-3'>
        <Table className='w-full'>
          <TableHeader className='sticky top-0 z-10 rounded-md bg-gray-3'>
            {/* Header Row */}
            <TableRow>
              {Array.from({ length: columnCount }).map((_, index) => (
                <TableHead
                  // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton component
                  key={`skeleton-header-${index}`}
                  className={cn(
                    'border-gray-6 border-t border-b bg-gray-3 px-4 text-left',
                    index === 0 && 'rounded-tl-md border-l',
                    index === columnCount - 1 && 'rounded-tr-md border-r',
                    !(index === 0 || index === columnCount - 1) && 'border-r-0 border-l-0'
                  )}
                >
                  <div className='flex items-center gap-2'>
                    <Skeleton className='h-4 w-24' />
                    <Skeleton className='size-4 shrink-0 rounded' />
                  </div>
                </TableHead>
              ))}
            </TableRow>
            {/* Filter Row */}
            <TableRow className='bg-gray-3'>
              {Array.from({ length: columnCount }).map((_, index) => (
                <TableCell
                  // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton component
                  key={`skeleton-filter-${index}`}
                  className={cn('border border-gray-6 bg-gray-2 px-4 py-2', index === columnCount - 1 && 'border-r')}
                >
                  {index === 0 ? (
                    <Skeleton className='h-8 w-full rounded-md' /> // Input filter
                  ) : (
                    <Skeleton className='h-8 w-full rounded-md' /> // Select filter
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Data Rows */}
            {Array.from({ length: rowCount }).map((_, rowIndex) => (
              <TableRow
                // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton component
                key={`skeleton-row-${rowIndex}`}
                isEven={rowIndex % 2 === 0}
              >
                {Array.from({ length: columnCount }).map((_, colIndex) => (
                  <TableCell
                    // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton component
                    key={`skeleton-cell-${rowIndex}-${colIndex}`}
                    className={cn('border border-gray-6 px-4 py-2', colIndex === columnCount - 1 && 'border-r')}
                  >
                    {colIndex === 0 ? (
                      <Skeleton className='h-4 w-20' /> // License plate (shorter)
                    ) : (
                      <Skeleton className='h-4 w-12' /> // Boolean value (shorter)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {/* Footer Row */}
            <TableRow>
              <TableCell colSpan={columnCount} className='border border-gray-6 px-4 py-3 text-center'>
                <Skeleton className='mx-auto h-4 w-48' />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const VehiclesListToolWithErrorBoundary = ({ part }: { part: any }) => (
  <ErrorBoundary
    fallback={<VehiclesListToolError />}
    onError={(error, errorInfo) => {
      console.error(error, errorInfo);
    }}
  >
    {part.output && <VehiclesListTool part={part} />}
  </ErrorBoundary>
);

export default VehiclesListToolWithErrorBoundary;
